import { useState } from "react"
import PropTypes from "prop-types"
import { size, map, startsWith, get, orderBy as lodashOrderBy, noop, isEmpty, first, filter, toLower } from "lodash"
import { Link, Table, TableBody, TableCell, TableContainer, TableRow, Checkbox } from "@mui/material"
import OPTooltip from "components/material/tooltip"
import { percentageFormatter } from "helpers/formatters/numbers"
import { INSTRUMENT_SIZE_SHAPE } from "modules/smart-trade-panel/helpers"
import EnhancedTableHead from "./EnhancedTableHead"
import EnhancedTableToolbar from "./EnhancedTableToolbar"

function simpleSearch(array = [], filterProp, search = "") {
  return filter(array, (item) => {
    const label = get(item, filterProp, "")
    return startsWith(toLower(label), toLower(search))
  })
}

const headCells = [
  { id: "ticker", numeric: false, disablePadding: true, label: "Ticker" },
  { id: "equity", numeric: true, disablePadding: false, label: "% Equity" },
]

const orderFieldMap = { ticker: "id", equity: "economicExposure" }

export default function EnhancedTable(props) {
  const [order, setOrder] = useState("asc")
  const [orderBy, setOrderBy] = useState()
  const [search, setSearch] = useState()
  const [selected, setSelected] = useState([])

  const { items, isReadOnly, removeSecurities, refSecurities, dispatch, instrumentPortfolio, instrumentSize, isSegment, openSecurityProfile2 } = props

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc"
    setOrder(isAsc ? "desc" : "asc")
    setOrderBy(property)
  }

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = items.map((n) => n.id)
      setSelected(newSelecteds)
      setSearch("")
      return
    }
    setSelected([])
  }

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name)
    let newSelected = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      )
    }

    setSelected(newSelected)
    setSearch("")
  }

  const isSelected = (name) => selected.indexOf(name) !== -1
  const onClickHandler = (security) => isReadOnly ? noop : (event) => handleClick(event, security.id)

  let stickySecurities
  let count = size(items)
  // Segments dont require Sticky Securities
  // When not viewing a segment, we need to add the ref instrument securities to the count
  if (!isSegment) {
    if (isEmpty(instrumentPortfolio)) {
      count += size(refSecurities)
      const security = first(refSecurities)
      stickySecurities = [{
        ...security,
        value: percentageFormatter(get(instrumentSize, "economicExposure"), true),
        action: () => openSecurityProfile2(security.assetClass, security.id, security.ticker, security.mic),
      }]
    }
  }

  return <div className="positions-table">
    <EnhancedTableToolbar
      items={items}
      count={count}
      numSelected={selected.length}
      selected={selected}
      removeSecurities={removeSecurities}
      setSelected={setSelected}
      setSearch={setSearch}
      instrumentSize={isEmpty(instrumentPortfolio) && instrumentSize}
    />
    <TableContainer className="table-root">
      <Table
        aria-labelledby="tableTitle"
        size="medium"
        aria-label="enhanced table"
      >
        <EnhancedTableHead
          headCells={headCells}
          numSelected={selected.length}
          order={order}
          orderBy={orderBy}
          onSelectAllClick={handleSelectAllClick}
          onRequestSort={handleRequestSort}
          rowCount={size(items)}
          isReadOnly={isReadOnly}
        />
        <TableBody>
          {map(stickySecurities, (security) => {
            let label
            if (Array.isArray(security.label)) {
              label = <OPTooltip title={first(security.label)}><span className="name-wrapper">{map(security.label, (fragment, i) => <span key={`label-${i}`}>{fragment}</span>)}</span></OPTooltip>
            } else {
              label = <span>{security.label}</span>
            }

            const securityId = `${get(security, "id.ticker")}.${get(security, "id.mic")}`
            return <TableRow key={securityId} hover role="checkbox" tabIndex={-1} className="sticky-securities">
              <TableCell className="sticky padding" />
              <TableCell className="sticky label" component="td" id={security.id} scope="row">
                {security.action ? <Link
                  href="#"
                  onClick={() => { dispatch(security.action()) }}
                  underline="hover"
                >
                  {label}
                </Link>
                  : label}
              </TableCell>
              <TableCell className="sticky value" align="right">
                {security.value}
              </TableCell>
            </TableRow>
          })}
          {lodashOrderBy(simpleSearch(items, "id", search), [orderFieldMap[orderBy]], [order])
            .map((security, index) => {
              const isItemSelected = isSelected(security.id)
              const labelId = `enhanced-table-checkbox-${index}`
              const securityId = `${get(security, "id.ticker")}.${get(security, "id.mic")}`
              return <TableRow
                hover
                onClick={onClickHandler(security)}
                role="checkbox"
                aria-checked={isItemSelected}
                tabIndex={-1}
                key={securityId}
                selected={isItemSelected}
                padding="normal"
              >
                <TableCell padding="checkbox">
                  {!isReadOnly
                    && <Checkbox
                      checked={isItemSelected}
                      inputProps={{ "aria-labelledby": labelId }}
                    />}
                </TableCell>
                <TableCell className="label" component="th" id={labelId} scope="row" padding="none">
                  <Link href="#" onClick={() => { dispatch(security.action) }} underline="hover">{security.label}</Link>
                </TableCell>
                <TableCell className="value" align="right">{security.value}</TableCell>
              </TableRow>
            })}
        </TableBody>
      </Table>
    </TableContainer>
  </div>
}

EnhancedTable.propTypes = {
  items: PropTypes.array.isRequired,
  refSecurities: PropTypes.array.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  removeSecurities: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  instrumentPortfolio: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  instrumentSize: INSTRUMENT_SIZE_SHAPE.isRequired,
  isSegment: PropTypes.bool.isRequired,
  openSecurityProfile2: PropTypes.func.isRequired,
}
